import { AsyncStockRequest } from '../../model/async-stock-request';
import { RowReader } from '../../helpers/row-reader';
import { BatchFetcher } from '../../helpers/batch-fetcher';
import { CartStateManager } from '../../helpers/cart-state-manager';
import { renderStockFields } from '../../helpers/stock-renderer';
import { pickCheapestVariant } from '../../helpers/pick-cheapest-variant';
import StockAndPriceData from '../../model/stock-and-price-data';
import ProductFlags from '../../model/product-flags';

function stockEndpointResolver(initialOrder: boolean): (request: AsyncStockRequest) => string {
  return (request: AsyncStockRequest) => {
    let path: string;
    if (initialOrder) {
      path = '/products/masterStock/initialOrder';
    } else {
      path = request.isMasterClass ? '/products/masterStock' : '/products/stock';
    }
    return `${window.ACC.config.encodedContextPath}${path}`;
  };
}

export default class AsyncStock {
  static readonly DELIVERY_LOADED_EVENT = 'asyncDeliveryLoaded';

  private readonly refreshButton: JQuery<HTMLButtonElement>;
  private requestInProgress = false;
  private readonly debounceMs = 400;
  private debounceTimers: Record<string, ReturnType<typeof setTimeout>> = {};

  private readonly fetcher: BatchFetcher<AsyncStockRequest, StockAndPriceData>;

  constructor(
    private readonly rows: JQuery = $('.js-asyncProductData'),
    initialOrder = false,
  ) {
    this.refreshButton = $('.js-asyncProductDataRefresh');
    this.fetcher = new BatchFetcher(stockEndpointResolver(initialOrder));

    if (this.rows.length) {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      this.loadAll();
      this.bindQuantityRefresh();
    }
    // eslint-disable-next-line @typescript-eslint/no-misused-promises
    this.refreshButton.on('click', () => this.loadAll());
  }

  static init(container: JQuery, initialOrder = false): AsyncStock {
    return new AsyncStock(container.find('.js-asyncProductData'), initialOrder);
  }

  static initAll(initialOrder = false): AsyncStock {
    return new AsyncStock($('.js-asyncProductData'), initialOrder);
  }

  private async loadAll(): Promise<void> {
    if (this.requestInProgress) return;
    this.requestInProgress = true;
    this.refreshButton.addClass('-icon-animated');

    const requests = RowReader.requestsFromRows(this.rows);
    const withVariants = requests.filter((r) => r.productVariantCodes);
    const withoutVariants = requests.filter((r) => !r.productVariantCodes);

    try {
      const codeCount: Record<string, number> = {};

      await Promise.all([
        this.fetcher.fetchChunked(
          withoutVariants,
          (chunk) => {
            chunk.forEach((data) => {
              const row = RowReader.resolveRow(this.rows, data.productCode, codeCount);
              this.applyToRow(row, data);
            });
          },
          (error) => this.handleError(error),
        ),
        ...withVariants.map((p) => this.loadVariants(p)),
      ]);
    } finally {
      document.body.dispatchEvent(new Event(AsyncStock.DELIVERY_LOADED_EVENT));
      this.refreshButton.removeClass('-icon-animated');
      this.requestInProgress = false;
    }
  }

  private async loadVariants(product: AsyncStockRequest): Promise<void> {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const variantRequests: AsyncStockRequest[] = product.productVariantCodes!
      .split(',')
      .map((code) => ({
        productCode: code,
        quantity: product.quantity,
        isMasterClass: product.isMasterClass,
      }));

    const allData: StockAndPriceData[] = [];

    await this.fetcher.fetchChunked(
      variantRequests,
      (chunk) => allData.push(...chunk),
      (error) => this.handleError(error),
    );

    const best = pickCheapestVariant(allData);
    if (best) {
      const row = this.rows.filter(`[data-code="${product.productCode}"]`);
      this.applyToRow(row, best);
    }
  }

  private applyToRow(row: JQuery, data: StockAndPriceData): void {
    const flags: ProductFlags = RowReader.readProductFlags(row);
    renderStockFields(row, data, flags);
    const qtyInput = RowReader.qtyInputFor(row[0]);
    CartStateManager.update(row, data, qtyInput);
  }

  private handleError(error: JQuery.jqXHR): void {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    if (error.responseJSON?.length) {
      window.NotificationHelper.clearNotifications();
      window.NotificationHelper.showError(error.responseJSON);
    }
  }

  private bindQuantityRefresh(): void {
    this.rows.each((_, el) => {
      const qtyInput = RowReader.qtyInputFor(el);
      if (!qtyInput?.data('reactive')) return;

      const id = el.dataset.entryNumber ?? el.dataset.code ?? '';
      qtyInput.on('input', () => {
        clearTimeout(this.debounceTimers[id]);
        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        this.debounceTimers[id] = setTimeout(async () => {
          const req = RowReader.requestFromRow(el);
          if (!req) return;
          const row = $(el);
          try {
            const [data] = await this.fetcher.fetchOne(req);
            this.applyToRow(row, data);
          } catch {
            // Errors handled inside handleError
          }
        }, this.debounceMs);
      });
    });
  }
}
